import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import DesktopLayout from "@/components/Layout/DesktopLayout";
import { api, formatApiError, tokenStore } from "@/api/client";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, FileText, Download, ExternalLink, History, Trash2, ArrowUpDown, Copy } from "lucide-react";
import { toast } from "sonner";
import Pagination, { usePaged } from "@/components/Pagination";

export default function Quotes() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [err, setErr] = useState("");
  const [showAllVersions, setShowAllVersions] = useState(false);
  const [fCustomer, setFCustomer] = useState("");
  const [fProject, setFProject] = useState("");
  const [fSales, setFSales] = useState("");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/quote/quotes"); setQuotes(data); }
      catch (e) { setErr(formatApiError(e)); }
    })();
  }, []);

  const remove = async (q) => {
    if (!window.confirm(`Permanently delete quote ${q.quote_number}?\n\nThis cannot be undone.`)) return;
    try {
      await api.delete(`/quote/quotes/${q.id}`);
      toast.success("Quote deleted");
      setQuotes((qs) => qs.filter((x) => x.id !== q.id));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const canDelete = ["super_admin", "sales", "sales_coordination", "dealer"].includes(user?.role);
  const canDuplicate = ["super_admin", "sales", "sales_coordination", "production_coordination", "dealer"].includes(user?.role);

  const duplicate = async (q) => {
    if (!window.confirm(`Duplicate quote ${q.quote_number}?\n\nA new quote is created with the same items — you can edit it right away.`)) return;
    try {
      const { data } = await api.post(`/quote/quotes/${q.id}/duplicate`);
      toast.success(`Created ${data.quote_number}`);
      navigate(`/quotes/${data.id}/edit`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const pdfUrl = (id) => `${api.defaults.baseURL}/quote/quotes/${id}/pdf?auth=${encodeURIComponent(tokenStore.get() || "")}`;

  // Group by root, latest visible by default
  const { displayed, byRootCount } = useMemo(() => {
    const counts = {};
    for (const q of quotes) {
      const root = q.root_quote_id || q.id;
      counts[root] = (counts[root] || 0) + 1;
    }
    const match = (q) => {
      if (fCustomer && !(q.customer_name || "").toLowerCase().includes(fCustomer.toLowerCase())) return false;
      if (fProject && !(q.subject || "").toLowerCase().includes(fProject.toLowerCase())) return false;
      if (fSales && !(q.sales_person_name || "").toLowerCase().includes(fSales.toLowerCase())) return false;
      return true;
    };
    if (showAllVersions) {
      const sorted = quotes.filter(match).slice().sort((a, b) => {
        const cmp = (a.created_at || "").localeCompare(b.created_at || "");
        return sortDir === "desc" ? -cmp : cmp;
      });
      return { displayed: sorted, byRootCount: counts };
    }
    const latest = {};
    for (const q of quotes) {
      const root = q.root_quote_id || q.id;
      const cur = latest[root];
      if (!cur || (q.version || 1) > (cur.version || 1)) latest[root] = q;
    }
    return {
      displayed: Object.values(latest).filter(match).sort((a, b) => {
        const cmp = (a.created_at || "").localeCompare(b.created_at || "");
        return sortDir === "desc" ? -cmp : cmp;
      }),
      byRootCount: counts,
    };
  }, [quotes, showAllVersions, fCustomer, fProject, fSales, sortDir]);

  useEffect(() => { setPage(1); },
      [showAllVersions, fCustomer, fProject, fSales, sortDir]);
  const pageRows = usePaged(displayed, page, 50);

  return (
    <DesktopLayout>
      <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="hk-tiny-label">Quoting</div>
          <h1 className="text-4xl font-black tracking-tighter" style={{ fontFamily: "Chivo" }}>Quotes</h1>
          <p className="text-slate-500 mt-2 text-sm">
            {displayed.length} {showAllVersions ? "version" : "quote"}{displayed.length === 1 ? "" : "s"} {user?.role === "dealer" ? "(yours)" : ""}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => setShowAllVersions((v) => !v)}
            className="inline-flex items-center gap-2 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 font-semibold px-4 py-2.5 rounded-sm text-sm"
            data-testid="quotes-toggle-versions"
          >
            <History className="w-4 h-4" /> {showAllVersions ? "Latest only" : "Show all versions"}
          </button>
          <Link to="/quotes/new" className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold px-5 py-2.5 rounded-sm" data-testid="quotes-new">
            <Plus className="w-4 h-4" /> New quote
          </Link>
        </div>
      </div>

      {err && <div className="text-red-600 text-sm">{err}</div>}

      <div className="flex flex-wrap gap-2 mb-3">
        <FilterInput label="Customer" value={fCustomer} onChange={setFCustomer} testId="quotes-filter-customer" />
        <FilterInput label="Project name" value={fProject} onChange={setFProject} testId="quotes-filter-project" />
        <FilterInput label="Sales person" value={fSales} onChange={setFSales} testId="quotes-filter-sales" />
        {(fCustomer || fProject || fSales) && (
          <button type="button" onClick={() => { setFCustomer(""); setFProject(""); setFSales(""); }}
            className="text-xs text-slate-500 hover:text-slate-800 underline underline-offset-2"
            data-testid="quotes-filter-clear">Clear filters</button>
        )}
      </div>

      <div className="hk-card overflow-x-auto">
        <table className="w-full text-sm min-w-[1100px]">
          <thead>
            <tr className="bg-slate-50 hk-tiny-label">
              <th className="text-left px-5 py-3">Quote #</th>
              <th className="text-left px-5 py-3">Ver</th>
              <th className="text-left px-5 py-3 min-w-[160px]">Customer</th>
              <th className="text-left px-5 py-3 min-w-[220px]">Project name</th>
              <th className="text-left px-5 py-3 min-w-[140px]">Sales person</th>
              <th className="text-left px-5 py-3">Items</th>
              <th className="text-right px-5 py-3">Grand Total</th>
              <th className="text-left px-5 py-3">Created by</th>
              <th className="text-left px-5 py-3">
                <button type="button" onClick={() => setSortDir((d) => (d === "desc" ? "asc" : "desc"))}
                  className="inline-flex items-center gap-1 hover:text-slate-800 uppercase text-[10px] font-bold tracking-wide"
                  data-testid="quotes-sort-date">
                  Date <ArrowUpDown className="w-3 h-3" />
                </button>
              </th>
              <th className="text-right px-5 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map((q) => {
              const root = q.root_quote_id || q.id;
              const versionCount = byRootCount[root] || 1;
              return (
                <tr key={q.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-5 py-3 font-mono">{q.quote_number}</td>
                  <td className="px-5 py-3 text-xs">
                    <span className="hk-badge hk-badge-progress">v{q.version || 1}</span>
                    {versionCount > 1 && !showAllVersions && (
                      <span className="ml-1 text-[10px] text-slate-500" data-testid={`versions-count-${q.id}`}>of {versionCount}</span>
                    )}
                  </td>
                  <td className="px-5 py-3 font-semibold whitespace-normal break-words">{q.customer_name}</td>
                  <td className="px-5 py-3 text-slate-600 whitespace-normal break-words">{q.subject || "—"}</td>
                  <td className="px-5 py-3 text-slate-700 whitespace-normal break-words" data-testid={`quote-sales-${q.id}`}>{q.sales_person_name || "—"}</td>
                  <td className="px-5 py-3">{(q.lines || []).length}</td>
                  <td className="px-5 py-3 text-right font-bold">₹{((q.totals || {}).grand_total || 0).toLocaleString()}</td>
                  <td className="px-5 py-3 text-xs">{q.created_by_name}</td>
                  <td className="px-5 py-3 text-xs">{new Date(q.created_at).toLocaleDateString()}</td>
                  <td className="px-5 py-3 text-right whitespace-nowrap">
                    <Link to={`/quotes/${q.id}`} className="text-orange-600 hover:text-orange-700 font-bold text-xs mr-3" data-testid={`quote-open-${q.quote_number}`}>
                      Open <ExternalLink className="w-3 h-3 inline" />
                    </Link>
                    <a href={pdfUrl(q.id)} target="_blank" rel="noopener noreferrer" className="text-slate-600 hover:text-slate-900 font-bold text-xs mr-3">
                      <Download className="w-3 h-3 inline" /> PDF
                    </a>
                    {canDuplicate && (
                      <button onClick={() => duplicate(q)} className="text-slate-500 hover:text-orange-600 font-bold text-xs mr-3" title="Duplicate quote" data-testid={`quote-duplicate-${q.quote_number}`}>
                        <Copy className="w-3 h-3 inline" /> Copy
                      </button>
                    )}
                    {canDelete && (
                      <button onClick={() => remove(q)} className="text-slate-400 hover:text-red-600" title="Delete quote" data-testid={`quote-delete-${q.quote_number}`}>
                        <Trash2 className="w-3.5 h-3.5 inline" />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {displayed.length === 0 && (
              <tr><td colSpan={9} className="px-5 py-10 text-center text-slate-500">
                <FileText className="w-8 h-8 mx-auto mb-2" /> No quotes yet — click &quot;New quote&quot;.
              </td></tr>
            )}
          </tbody>
        </table>
        <Pagination total={displayed.length} pageSize={50} page={page} onChange={setPage} testId="quotes-pagination" />
      </div>
    </DesktopLayout>
  );
}

function FilterInput({ label, value, onChange, testId }) {
  return (
    <div className="relative">
      <input type="text" value={value} onChange={(e) => onChange(e.target.value)}
        placeholder={label}
        className="bg-white border border-slate-300 rounded-sm pl-3 pr-8 py-1.5 text-xs w-52 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
        data-testid={testId} />
      {value && (
        <button type="button" onClick={() => onChange("")}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-800 text-xs">×</button>
      )}
    </div>
  );
}
